import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { CustomerTitle } from '@core/enum/customerTitle';
import { optionalNumber, optionalTrimmed, toFormValue } from '@core/utils/form';
import type { CreateCustomerData, Customer } from '../../api/customer';
import {
  createCustomer,
  customerKeys,
  updateCustomer,
} from '../../api/customer';
import { toMeasurementNumber } from '../utils/measurement';

export interface CustomerMeasurementFormValues {
  length: string;
  shoulder: string;
  sleeve: string;
  neck: string;
  chest: string;
  waist: string;
  figure: string;
  head: string;
  chasubleLength: string;
  chasubleSleeve: string;
}

export interface CustomerFormValues extends CustomerMeasurementFormValues {
  title: CustomerTitle | '';
  name: string;
  diocese: string;
  address: string;
  email: string;
  phone: string;
}

function measurementFormValues(
  customer?: Customer | null
): CustomerMeasurementFormValues {
  return {
    length: toFormValue(customer?.length),
    shoulder: toFormValue(customer?.shoulder),
    sleeve: toFormValue(customer?.sleeve),
    neck: toFormValue(customer?.neck),
    chest: toFormValue(customer?.chest),
    waist: toFormValue(customer?.waist),
    figure: toFormValue(customer?.figure),
    head: toFormValue(customer?.head),
    chasubleLength: toFormValue(customer?.chasubleLength),
    chasubleSleeve: toFormValue(customer?.chasubleSleeve),
  };
}

function measurementPayload(
  data: CustomerMeasurementFormValues
): Pick<CreateCustomerData, keyof CustomerMeasurementFormValues> {
  return {
    length: toMeasurementNumber(data.length),
    shoulder: toMeasurementNumber(data.shoulder),
    sleeve: toMeasurementNumber(data.sleeve),
    neck: toMeasurementNumber(data.neck),
    chest: toMeasurementNumber(data.chest),
    waist: toMeasurementNumber(data.waist),
    figure: toMeasurementNumber(data.figure),
    head: toMeasurementNumber(data.head),
    chasubleLength: toMeasurementNumber(data.chasubleLength),
    chasubleSleeve: toMeasurementNumber(data.chasubleSleeve),
  };
}

function customerPayload(data: CustomerFormValues): CreateCustomerData {
  return {
    title: data.title || undefined,
    name: data.name.trim(),
    diocese: optionalNumber(data.diocese),
    address: optionalTrimmed(data.address),
    email: optionalTrimmed(data.email),
    phone: optionalTrimmed(data.phone),
    ...measurementPayload(data),
  };
}

export function useCustomerForm(
  customer?: Customer | null,
  inputValue?: string
) {
  const queryClient = useQueryClient();
  const measurementDefaultValues = useMemo(
    () => measurementFormValues(customer),
    [customer]
  );
  const defaultValues = useMemo<CustomerFormValues>(
    () => ({
      title: customer?.title ?? '',
      name: customer?.name ?? inputValue?.trim() ?? '',
      diocese: String(customer?.diocese?.id ?? ''),
      address: customer?.address ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? '',
      ...measurementDefaultValues,
    }),
    [customer, inputValue, measurementDefaultValues]
  );

  const save = useCallback(
    async (data: CustomerFormValues) => {
      const payload = customerPayload(data);
      const saved = customer
        ? await updateCustomer(customer.id, {
            ...payload,
            profilePicture: customer.profilePicture || undefined,
          })
        : await createCustomer(payload);
      await queryClient.invalidateQueries({ queryKey: customerKeys.all });
      return saved;
    },
    [customer, queryClient]
  );

  const saveMeasurements = useCallback(
    async (data: CustomerMeasurementFormValues) => {
      if (!customer) {
        return undefined;
      }
      const saved = await updateCustomer(customer.id, {
        title: customer.title || undefined,
        name: customer.name,
        diocese: customer.diocese?.id,
        address: optionalTrimmed(customer.address ?? ''),
        email: optionalTrimmed(customer.email ?? ''),
        phone: optionalTrimmed(customer.phone ?? ''),
        profilePicture: customer.profilePicture || undefined,
        ...measurementPayload(data),
      });
      await queryClient.invalidateQueries({ queryKey: customerKeys.all });
      return saved;
    },
    [customer, queryClient]
  );

  return { defaultValues, measurementDefaultValues, save, saveMeasurements };
}
